import { useEffect, useRef, useState } from "react";
import styles from "./CommunityContentPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import { FaSpinner } from "react-icons/fa";

/* ---------------- TEXT FORMATTER ---------------- */
const formatBoldText = (text = "") =>
  text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

/* ---------------- THUMBNAILS ---------------- */
const getYoutubeThumbnail = (url) => {
  const match = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

const getVimeoThumbnail = async (url) => {
  // Handle both public and private Vimeo URLs
  // Public: vimeo.com/123456789
  // Private: vimeo.com/123456789/abc123def456
  // With query params: vimeo.com/123456789/abc123def456?share=copy
  const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
  const match = url.match(regex);

  if (!match || !match[1]) return null;

  const videoId = match[1];

  try {
    // Use the full URL for oEmbed API (works for both public and private)
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
    );

    if (response.ok) {
      const data = await response.json();
      if (data.thumbnail_url) {
        return data.thumbnail_url
          .replace("_295x166", "_640x360")
          .replace("_640", "_1280");
      }
    }
  } catch (error) {
    console.error("Vimeo API error:", error);
  }

  // Fallback to vumbnail service
  return `https://vumbnail.com/${videoId}.jpg`;
};

const getVideoThumbnail = async (url) => {
  if (url.includes("vimeo")) return await getVimeoThumbnail(url);
  if (url.includes("youtube") || url.includes("youtu.be"))
    return getYoutubeThumbnail(url);
  return videoPlaceholderImage;
};

/* ================================================= */

const CommunityContentPage = () => {
  const { communityIndex, communities } = useDashboardStore();
  const { user, userMeta } = useAuthStore();
  const scrollRef = useRef(null);

  const [thumbnailCache, setThumbnailCache] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // 🔐 Membership purchase states (same as ZoomMeetingCard)
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const community = communities[communityIndex];
  const role = user?.roles?.[0];

  let userMembershipIds = [];
  if (Array.isArray(userMeta?.membership_ids)) {
    userMembershipIds = userMeta.membership_ids.map(Number);
  } else if (typeof userMeta?.membership_ids === "string") {
    try {
      userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
    } catch {
      userMembershipIds = [];
    }
  }

  /* ---------------- PRELOAD THUMBNAILS ---------------- */
  useEffect(() => {
    if (!community?.video_links?.length) return;

    const load = async () => {
      const cache = {};
      await Promise.all(
        community.video_links.map(async (v, i) => {
          cache[i] = (await getVideoThumbnail(v.url)) || videoPlaceholderImage;
        })
      );
      setThumbnailCache(cache);
    };

    load();
  }, [community]);

  /* ---------------- PURCHASE ---------------- */
  const handleMembershipClick = async (membershipIds) => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      if (!membershipIds?.length) {
        toast.error("No membership plans configured for this video");
        return;
      }

      if (membershipIds.length === 1) {
        handlePurchaseMembership(membershipIds[0]);
      } else {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        const allPlans = data.memberships || [];

        const filteredPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id)
        );

        setAvailablePlans(filteredPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error loading membership plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link"
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase");
    } finally {
      setIsPurchasing(false);
    }
  };

  /* ---------------- VIDEO CLICK ---------------- */
  const handleVideoClick = async (video) => {
    let hasAccess = true;

    if (video.membershipIds?.length) {
      if (role === "administrator") {
        hasAccess = true;
      } else if (role === "trader") {
        // Check if trader is assigned to any channel in this community
        hasAccess = await checkTraderAccessToCommunity();
      } else {
        // Regular user - check memberships
        // ✅ Normalize video membership IDs to integers
        const normalizedVideoMembershipIds = (video.membershipIds || [])
          .map((id) => {
            const num = Number(id);
            return isNaN(num) ? 0 : num;
          })
          .filter((id) => id > 0);

        // ✅ User membership IDs are already normalized at lines 88-96
        hasAccess = normalizedVideoMembershipIds.some((id) =>
          userMembershipIds.includes(id)
        );
      }
    }

    if (!hasAccess) {
      toast.error("Video is restricted");
      handleMembershipClick(video.membershipIds);
      return;
    }

    if (video.url.includes("vimeo")) {
      // Extract video ID and hash for private videos
      const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
      const match = video.url.match(regex);

      if (match && match[1]) {
        const videoId = match[1];
        const hash = match[2];

        setSelectedVideo({
          type: "vimeo",
          id: videoId,
          hash: hash,
        });
        setShowVideoModal(true);
      }
    } else {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
  };
  /* ---------------- CHECK TRADER ACCESS TO COMMUNITY ---------------- */
  const checkTraderAccessToCommunity = async () => {
    try {
      const { channels } = useDashboardStore.getState();

      // Get all channels in this community
      const communityChannels = channels.filter(
        (channel) => channel.community === community.name
      );

      // console.log("Community name:", community.name);
      // console.log("Found channels in community:", communityChannels.length);

      if (communityChannels.length === 0) {
        return false;
      }

      // Get all channel IDs
      const channelIds = communityChannels.map((c) => c.id);
      // console.log("Channel IDs to check:", channelIds);

      // Single API call for all channels
      const { data } = await api.post(ENDPOINTS.GET_CHANNELS_BY_IDS, {
        channelIds,
      });

      if (data.success) {
        const channelsData = data.channels || [];
        // console.log("Received channel details:", channelsData.length);

        // Check if trader is assigned to any community channel
        const isAssignedToAnyCommunityChannel = channelsData.some((channel) => {
          const traderIds = channel.trader_ids || [];
          return traderIds.includes(user.id);
        });

        console.log(
          "Trader assigned to any channel:",
          isAssignedToAnyCommunityChannel
        );
        return isAssignedToAnyCommunityChannel;
      }

      return false;
    } catch (error) {
      console.error("Error checking trader access to community:", error);
      return false;
    }
  };

  /* ---------------- SCROLL ---------------- */
  const scrollLeft = () =>
    scrollRef.current.scrollBy({ left: -600, behavior: "smooth" });
  const scrollRight = () =>
    scrollRef.current.scrollBy({ left: 600, behavior: "smooth" });

  const hasContent =
    community.content_title ||
    community.content_primary_text ||
    community.content_secondary_text ||
    community.content_banner ||
    community.content_image ||
    community.video_links?.length;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>{community.name}</h2>
      </div>

      <div className={styles.scrollableContent}>
        {!hasContent && <p>No content available for this community</p>}

        {community.content_banner && (
          <img
            src={ENDPOINTS.IMAGE_BASE_URL + community.content_banner}
            className={styles.bannerImage}
            alt="Banner"
          />
        )}

        {community.content_title && (
          <h2
            className={styles.subtitle}
            dangerouslySetInnerHTML={{
              __html: formatBoldText(community.content_title),
            }}
          />
        )}

        {community.content_primary_text && (
          <div
            className={styles.descriptionContainer}
            dangerouslySetInnerHTML={{
              __html: formatBoldText(community.content_primary_text),
            }}
          />
        )}

        {community.content_image && (
          <img
            src={ENDPOINTS.IMAGE_BASE_URL + community.content_image}
            className={styles.bannerImage}
            alt="Banner"
          />
        )}

        {community.content_secondary_text && (
          <div
            className={styles.descriptionContainer}
            dangerouslySetInnerHTML={{
              __html: formatBoldText(community.content_secondary_text),
            }}
          />
        )}

        {/* ---------------- VIDEOS ---------------- */}
        {community.video_links?.length > 0 && (
          <div className={styles.videoSection}>
            <h2 className={styles.subtitle}>Watch videos</h2>

            <div className={styles.scrollContainer}>
              <button className={styles.arrow} onClick={scrollLeft}>
                ❮
              </button>

              <div className={styles.videoList} ref={scrollRef}>
                {community.video_links.map((video, i) => (
                  <div
                    key={i}
                    className={styles.videoCardWrapper}
                    onClick={() => handleVideoClick(video)}
                  >
                    <img
                      src={thumbnailCache[i] || videoPlaceholderImage}
                      className={styles.videoCard}
                      alt={video.title}
                    />
                    <div className={styles.videoOverlay}>
                      <div className={styles.playIcon}>▶</div>
                    </div>
                    {video.title && (
                      <div className={styles.videoTitle}>{video.title}</div>
                    )}
                  </div>
                ))}
              </div>

              <button className={styles.arrow} onClick={scrollRight}>
                ❯
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---------------- PLAN SELECTION MODAL ---------------- */}
      {showPlanSelection && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <h3>Choose a Membership Plan</h3>

            {availablePlans.map((plan) => (
              <div key={plan.id} style={{ marginBottom: 16 }}>
                <h4>{plan.name || plan.label}</h4>
                <p>
                  {plan.payment_type === "free" ? "Free" : `$${plan.price}`}
                </p>
                <button
                  className={styles.popupButton}
                  disabled={isPurchasing}
                  onClick={() => {
                    handlePurchaseMembership(plan.id);
                    setShowPlanSelection(false);
                  }}
                >
                  {isPurchasing ? (
                    <>
                      <FaSpinner className="fa-spin" /> Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </button>
              </div>
            ))}

            <button
              className={styles.popupButton}
              onClick={() => setShowPlanSelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ---------------- VIMEO MODAL ---------------- */}
      {showVideoModal && selectedVideo && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        >
          <div
            className={styles.videoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              ✕
            </button>
            {selectedVideo.type === "vimeo" && (
              <iframe
                src={
                  selectedVideo.hash
                    ? `https://player.vimeo.com/video/${selectedVideo.id}?h=${selectedVideo.hash}&autoplay=1&title=0&byline=0&portrait=0&controls=1`
                    : `https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0&controls=1`
                }
                className={styles.videoIframe}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityContentPage;
